import React, { useEffect, useState } from "react";
import { ScreenProps } from "../../SequenceController";
import { useLanguage } from "../../../../context/LanguageContext";
import { EndowmentEffect } from "../../../../translations/experiments/EndowmentEffect";
import { colors, commonStyles } from "../../../../components/colors";
import EndowmentImg from "./assets/Endowment.png";

/**
 * EndowmentEffectScreen1 - Käufer*innen (WTP) – volle Euro + verpflichtende Begründung
 */
export const EndowmentEffectScreen1: React.FC<ScreenProps> = ({ setCanContinue, result, setResult }) => {
    const { language } = useLanguage();
    const t = EndowmentEffect[language].screens.buyer;

    const [price, setPrice] = useState<string>("");
    const [priceError, setPriceError] = useState<string>("");
    const [reason, setReason] = useState<string>("");
    const [reasonTouched, setReasonTouched] = useState<boolean>(false);
    const [reasonError, setReasonError] = useState<string>("");

    useEffect(() => {
        // Preisvalidierung: ganze Euro >= 0
        const raw = price.trim();
        const asNumber = raw.length > 0 ? Number(raw.replace(",", ".")) : NaN;
        const priceOk = !isNaN(asNumber) && Number.isFinite(asNumber) && Number.isInteger(asNumber) && asNumber >= 0;

        if (!priceOk && price.length > 0) {
            setPriceError(t.validation);
        } else {
            setPriceError("");
        }

        // Begründung validieren: nicht leer
        const reasonOk = reason.trim().length > 0;
        if (!reasonOk && reasonTouched) {
            setReasonError(t.reasonRequired || "(erforderlich)");
        } else {
            setReasonError("");
        }

        const ok = priceOk && reasonOk;
        setCanContinue(!!ok);

        if (ok) {
            setResult((prev: any) => ({
                ...(prev || {}),
                role: "buyer",
                willingToPay: Math.trunc(asNumber),
                currency: "EUR",
                reason: reason.trim()
            }));
            setPriceError("");
            setReasonError("");
        } else {
            // keep previous result but don't overwrite if incomplete
            setResult((prev: any) => ({ ...(prev || {}) }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [price, reason, reasonTouched]);

    return (
        <div className="experiment-container" style={commonStyles.container}>
            <div style={commonStyles.card}>
                <h2 style={{
                    color: colors.brillantBlue,
                    margin: "0 0 24px 0",
                    fontSize: 28,
                    fontWeight: 700,
                    textAlign: "center"
                }}>
                    {EndowmentEffect[language].title}
                </h2>

                <div style={{
                    ...commonStyles.paragraph,
                    textAlign: "justify",
                    textJustify: "inter-word"
                }}>
                    <p style={{ margin: 0 }}>{t.text}</p>
                </div>

                <div style={{
                    textAlign: "center",
                    margin: "28px 0 18px 0",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center"
                }}>
                    <img
                        src={EndowmentImg}
                        alt={language === "de" ? "Festivalposter Seestadt Soundfestival" : "Seestadt Soundfestival poster"}
                        style={{
                            width: "100%",
                            maxWidth: 520,
                            height: "auto",
                            maxHeight: 360,
                            objectFit: "cover",
                            borderRadius: 16,
                            boxShadow: "0 6px 20px 0 rgba(0,0,0,0.09)",
                            display: "inline-block"
                        }}
                    />
                </div>

                <div style={{ textAlign: "center", margin: "24px 0 12px 0" }}>
                    <span style={{ color: colors.brillantBlue, fontWeight: 700, fontSize: 16 }}>
                        {t.inputLabel}
                    </span>
                </div>

                <div style={{ textAlign: "center", margin: "10px 0 8px 0" }}>
                    <input
                        aria-label={t.inputLabel}
                        type="number"
                        step={1}
                        min={0}
                        value={price}
                        placeholder={t.placeholder}
                        onChange={e => setPrice(e.target.value)}
                        style={{
                            width: 240,
                            maxWidth: "80%",
                            fontSize: 18,
                            padding: "12px 14px",
                            borderRadius: 10,
                            border: "1px solid #e1e6ea",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                            textAlign: "center"
                        }}
                    />
                    {priceError && <div style={{ color: 'red', marginTop: 10 }}>{priceError}</div>}
                </div>

                {/* Begründung (verpflichtend) */}
                <div style={{ marginTop: 18 }}>
                    <label style={{ display: "block", fontWeight: 600, color: colors.textDark, marginBottom: 8 }}>
                        {t.reasonLabel} <span style={{ fontWeight: 500, color: colors.textMedium }}>{t.reasonRequired}</span>
                    </label>
                    <textarea
                        aria-label={t.reasonLabel}
                        placeholder={t.reasonPlaceholder}
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        onBlur={() => setReasonTouched(true)}
                        rows={4}
                        style={{
                            width: "100%",
                            maxWidth: 640,
                            minHeight: 96,
                            padding: 12,
                            fontSize: 15,
                            borderRadius: 8,
                            border: "1px solid #e1e6ea",
                            resize: "vertical",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.03)"
                        }}
                    />
                    {reasonError && <div style={{ color: 'red', marginTop: 8 }}>{reasonError}</div>}
                </div>

                <div style={{ fontSize: 14, color: colors.textMedium, textAlign: "center", marginTop: 12 }}>
                    {language === "de"
                        ? "Bitte gib den Betrag in vollen Euro (ohne Cent) an und begründe kurz deine Entscheidung."
                        : "Please enter the amount in whole euros (no cents) and briefly explain your choice."}
                </div>
            </div>
        </div>
    );
};